import type { FC, ReactNode } from 'react'
import type { TrendPoint } from '@/components/dashboard/attendance-trend'
import type { DonutSegment } from '@/components/dashboard/status-donut'

import Link from 'next/link'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader } from '@/components/ui/card'
import { Avatar, Progress } from '@/components/ui/feedback'
import { StatTile } from '@/components/app/stat-tile'
import { DashboardHero } from '@/components/dashboard/dashboard-hero'
import { AttendanceTrend } from '@/components/dashboard/attendance-trend'
import { StatusDonut } from '@/components/dashboard/status-donut'
import { semesterLabel, useAcademic } from '@/components/providers/academic-provider'

import {
    ArrowRight,
    BookOpen,
    CalendarDays,
    ChartLine,
    CircleCheck,
    ClipboardCheck,
    ClipboardX,
    School,
    Sparkles,
    Users
} from 'lucide-react'

import {
    DEMO_TODAY,
    dayNumber,
    formatDate,
    shortMonthName
} from '@/lib/dates'

import {
    average,
    countStatuses,
    formatDecimal,
    percent,
    uniqueSorted
} from '@/lib/academic'

const SectionLink: FC<{ href: string, children: ReactNode }> = ({ href, children }): ReactNode => {
    return (
        <Link
            href={href}
            className='inline-flex shrink-0 items-center gap-1 rounded-md text-[13px] font-semibold text-primary outline-none hover:underline hover:underline-offset-4 focus-visible:ring-3 focus-visible:ring-ring/40'
        >
            {children}
            <ArrowRight className='size-3.5' aria-hidden='true' />
        </Link>
    )
}

const STATUS_CHIPS = [
    { key: 'H', label: 'Hadir', dot: 'bg-success' },
    { key: 'S', label: 'Sakit', dot: 'bg-warning' },
    { key: 'I', label: 'Izin', dot: 'bg-info' },
    { key: 'A', label: 'Alpa', dot: 'bg-destructive' }
] as const

const Dashboard: FC = (): ReactNode => {
    const { attendance, grades, students, classes, subjects, studentById, classById, selectedYear } = useAcademic()

    const today = useMemo(() => {
        const records = attendance.filter(record => record.date === DEMO_TODAY)
        const counts = countStatuses(records)
        const total = students.length
        const recordedClasses = new Set(records.map(record => record.classId))

        return {
            counts,
            total,
            recorded: records.length,
            remaining: Math.max(0, total - records.length),
            rate: percent(counts.H, total),
            missingClasses: classes.filter(classRoom => !recordedClasses.has(classRoom.id)).map(classRoom => classRoom.name)
        }
    }, [attendance, students, classes])

    const recent = useMemo<TrendPoint[]>(() => {
        return uniqueSorted(attendance.map(record => record.date), 'desc')
            .slice(0, 5)
            .map(date => {
                const rows = attendance.filter(record => record.date === date)
                const present = rows.filter(record => record.status === 'H').length

                return { date, present, total: rows.length, rate: percent(present, rows.length) }
            })
    }, [attendance])

    const attention = useMemo(() => {
        const absences = new Map<string, number>()

        attendance.forEach(record => {
            if (record.status === 'A') absences.set(record.studentId, (absences.get(record.studentId) ?? 0) + 1)
        })

        return [...absences.entries()]
            .filter(([studentId]) => studentById.has(studentId))
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([studentId, count]) => ({ student: studentById.get(studentId)!, count }))
    }, [attendance, studentById])

    const gradeSummary = useMemo(() => {
        const passed = grades.filter(grade => grade.score >= (grade.kkm ?? 75)).length
        const mean = average(grades.map(grade => grade.score))

        return {
            average: mean === null ? null : Math.round(mean * 10) / 10,
            count: grades.length,
            kkmRate: percent(passed, grades.length)
        }
    }, [grades])

    const donutSegments: DonutSegment[] = [
        { key: 'H', label: 'Hadir', value: today.counts.H, stroke: 'stroke-success', fill: 'bg-success' },
        { key: 'S', label: 'Sakit', value: today.counts.S, stroke: 'stroke-warning', fill: 'bg-warning' },
        { key: 'I', label: 'Izin', value: today.counts.I, stroke: 'stroke-info', fill: 'bg-info' },
        { key: 'A', label: 'Alpa', value: today.counts.A, stroke: 'stroke-destructive', fill: 'bg-destructive' },
        { key: 'pending', label: 'Belum', value: today.remaining, stroke: 'stroke-muted-foreground/25', fill: 'bg-muted-foreground/40' }
    ]

    const trend = [...recent].reverse()

    return (
        <>
            <Seo />

            <div className='space-y-6'>
                <DashboardHero />

                <section aria-label='Ringkasan data akademik' className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    <StatTile
                        href='/siswa'
                        icon={<Users />}
                        tone='primary'
                        label='Total siswa'
                        value={students.length}
                        hint='Kelola direktori siswa'
                    />

                    <StatTile
                        href='/siswa'
                        icon={<School />}
                        tone='success'
                        label='Rombongan kelas'
                        value={classes.length}
                        hint='Kelola kelas di menu siswa'
                    />

                    <StatTile
                        href='/mapel'
                        icon={<BookOpen />}
                        tone='warning'
                        label='Mata pelajaran'
                        value={subjects.length}
                        hint='Standar KKM & mapel'
                    />

                    <StatTile
                        href='/absen'
                        icon={<ClipboardCheck />}
                        tone='info'
                        label='Absensi hari ini'
                        value={(
                            <>
                                {today.counts.H}
                                <span className='text-base font-semibold text-muted-foreground'> / {today.total}</span>
                            </>
                        )}
                        hint={`${today.remaining} siswa belum diabsen`}
                    />
                </section>

                <Card>
                    <div className='flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between'>
                        <div className='min-w-0'>
                            <p className='text-[11px] font-bold tracking-[0.14em] text-primary uppercase'>
                                Kehadiran hari ini
                            </p>

                            <h2 className='mt-1.5 flex flex-wrap items-baseline gap-x-2 text-foreground'>
                                <span className='text-4xl leading-none font-bold tracking-tight'>
                                    {today.rate}%
                                </span>

                                <span className='text-lg font-semibold'>
                                    Siswa Hadir
                                </span>
                            </h2>

                            <p className='mt-2 text-sm text-muted-foreground'>
                                {today.counts.H} siswa tercatat hadir dari {today.total} total siswa terdaftar.
                            </p>
                        </div>

                        {today.remaining > 0 ? (
                            <Link
                                href='/absen'
                                className='inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-destructive/25 bg-destructive/8 px-3.5 py-2 text-[13px] font-semibold text-destructive transition-colors outline-none hover:bg-destructive/12 focus-visible:ring-3 focus-visible:ring-destructive/30'
                            >
                                Lengkapi Absensi ({today.remaining} Belum)
                                <ArrowRight className='size-3.5' aria-hidden='true' />
                            </Link>
                        ) : (
                            <Badge tone='success' className='self-start px-3 py-1'>
                                <CircleCheck className='size-3.5' aria-hidden='true' />
                                Absensi lengkap
                            </Badge>
                        )}
                    </div>

                    <div className='px-5 pb-5'>
                        <Progress value={percent(today.counts.H, today.total)} tone='success' className='h-2.5' label='Persentase siswa hadir hari ini' />

                        <ul className='mt-4 flex flex-wrap gap-2'>
                            {STATUS_CHIPS.map(chip => (
                                <li key={chip.key} className='inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-[13px] text-muted-foreground'>
                                    <span className={cn('size-2 rounded-full', chip.dot)} aria-hidden='true' />
                                    {chip.label}:
                                    <strong className='font-bold text-foreground'>{today.counts[chip.key]}</strong>
                                </li>
                            ))}

                            <li className='inline-flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-[13px] text-muted-foreground'>
                                <span className='size-2 rounded-full bg-muted-foreground/40' aria-hidden='true' />
                                Belum Diabsen:
                                <strong className='font-bold text-foreground'>{today.remaining}</strong>
                            </li>
                        </ul>
                    </div>
                </Card>

                <div className='grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(19rem,1fr)]'>
                    <Card>
                        <CardHeader
                            eyebrow='Grafik kehadiran'
                            title='Tren 5 Hari Terakhir'
                            description='Persentase siswa hadir berdasarkan catatan absensi setiap tanggal.'
                            action={<Badge tone='primary'>5 hari belajar</Badge>}
                        />

                        <AttendanceTrend points={trend} />
                    </Card>

                    <Card className='flex flex-col'>
                        <CardHeader
                            eyebrow='Ringkasan hari ini'
                            title='Status Kehadiran'
                            description='Komposisi kehadiran siswa hari ini.'
                            action={<Badge tone='success'>{formatDate(DEMO_TODAY, 'dayMonth')}</Badge>}
                        />

                        <div className='flex-1 p-5'>
                            <StatusDonut
                                segments={donutSegments}
                                total={today.total}
                                centerValue={`${percent(today.recorded, today.total)}%`}
                                centerLabel='terdata'
                            />
                        </div>

                        <div className='border-t px-5 py-3.5'>
                            <SectionLink href='/absen'>Buka absensi</SectionLink>
                        </div>
                    </Card>
                </div>

                <section aria-label='Informasi tambahan' className='grid gap-4 md:grid-cols-3'>
                    {gradeSummary.average !== null && (
                        <StatTile
                            href='/nilai'
                            icon={<ChartLine />}
                            tone='primary'
                            label='Rata-rata nilai'
                            value={formatDecimal(gradeSummary.average, 1, true)}
                            hint={`${gradeSummary.count} data · KKM ${gradeSummary.kkmRate}%`}
                        />
                    )}

                    <StatTile
                        icon={<CalendarDays />}
                        tone='success'
                        label='Tahun ajaran'
                        value={<span className='text-lg'>{selectedYear ? semesterLabel(selectedYear) : '—'}</span>}
                        hint='Periode aktif'
                    />

                    {today.missingClasses.length > 0 && (
                        <StatTile
                            href='/absen'
                            icon={<ClipboardX />}
                            tone='danger'
                            label='Belum absen hari ini'
                            value={(
                                <span className='block truncate text-base'>
                                    {today.missingClasses.slice(0, 3).join(', ')}
                                    {today.missingClasses.length > 3 ? '…' : ''}
                                </span>
                            )}
                            hint={`${today.missingClasses.length} kelas`}
                            className='border-destructive/25'
                        />
                    )}
                </section>

                <div className='grid gap-6 lg:grid-cols-2'>
                    <Card>
                        <CardHeader
                            title='Riwayat Absensi Terakhir'
                            description='Kehadiran 5 hari pembelajaran terakhir'
                            action={<SectionLink href='/rekap'>Semua Rekap</SectionLink>}
                        />

                        {recent.length > 0 ? (
                            <ul className='divide-y'>
                                {recent.map(point => (
                                    <li key={point.date} className='flex items-center gap-3.5 px-5 py-3'>
                                        <span className='flex size-11 shrink-0 flex-col items-center justify-center rounded-lg border border-primary/20 bg-primary/8 leading-none text-primary'>
                                            <span className='text-sm font-bold'>
                                                {dayNumber(point.date)}
                                            </span>

                                            <span className='mt-0.5 text-[10px] font-semibold uppercase'>
                                                {shortMonthName(point.date)}
                                            </span>
                                        </span>

                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate text-sm font-semibold text-foreground'>
                                                {formatDate(point.date, 'medium')}
                                            </span>

                                            <span className='block text-xs text-muted-foreground'>
                                                {point.present} hadir dari {point.total} siswa tercatat ({point.rate}%)
                                            </span>
                                        </span>

                                        <Badge tone='neutral'>
                                            Tersimpan
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className='px-5 py-10 text-center text-sm text-muted-foreground'>
                                Belum ada riwayat absensi.
                            </p>
                        )}
                    </Card>

                    <Card>
                        <CardHeader
                            title='Siswa Perlu Diperhatikan'
                            description='Siswa dengan frekuensi alpa / tanpa keterangan'
                            action={<SectionLink href='/siswa'>Direktori Siswa</SectionLink>}
                        />

                        {attention.length > 0 ? (
                            <ul className='divide-y'>
                                {attention.map(({ student, count }) => (
                                    <li key={student.id} className='flex items-center gap-3 px-5 py-3'>
                                        <Avatar name={student.name} />

                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate text-sm font-semibold text-foreground'>
                                                {student.name}
                                            </span>

                                            <span className='block text-xs text-muted-foreground'>
                                                Kelas {classById.get(student.classId)?.name ?? '-'}
                                            </span>
                                        </span>

                                        <Badge tone='danger'>
                                            {count} alpa
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className='flex flex-col items-center justify-center gap-2 px-5 py-10 text-center'>
                                <span className='grid size-11 place-items-center rounded-full bg-success/12 text-success' aria-hidden='true'>
                                    <Sparkles className='size-5' />
                                </span>

                                <p className='text-sm font-semibold text-success'>
                                    Luar biasa! Tidak ada siswa dengan riwayat alpa.
                                </p>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </>
    )
}

export default Dashboard
